import type { RangeEntryWrapper } from '../../Common/Components/Selects/RangeCombo/Types'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import {EMPTY_STRING, COMMA} from '../../Common/Constants/Separators'

/**
 * Converts {@link RangeEntryWrapper} selections into a compact range filter expression string suitable
 * for use as a query parameter (e.g. {@code filterDeviceRange} or {@code filterAgentRange}).
 *
 * <p>Skips excluded entries ({@code included === false}). Numeric ranges emit a {@code min-max} segment
 * using display values; non-range entries emit the entry value as a string. Segments are joined by
 * {@code ,}.</p>
 *
 * <p>Example output: {@code "50-70,100,200-250"}</p>
 *
 * @param ranges - Selected range wrappers from {@link RangeCombo}, or {@code null} / empty when cleared.
 * @returns Compact filter expression, or an empty string when there are no included entries.
 */
export const buildRangeFilterExpression = (ranges: Nullable<RangeEntryWrapper[]>): string => {
    if (IsVarEmpty(ranges) || ArrayUtils.isEmpty(ranges)) {
        return EMPTY_STRING
    }

    return ranges
        .filter((wrapper: RangeEntryWrapper): boolean => wrapper.included !== false)
        .map((wrapper: RangeEntryWrapper): string => {
            if (wrapper.isRange) return `${wrapper.value.minDisplayValue}-${wrapper.value.maxDisplayValue}`

            return String(wrapper.value)
        })
        .filter((segment: string): boolean => segment.length > 0)
        .join(COMMA)
}
